/**
 * The PIN on a cabinet the user fixed — a width typed in its panel or set by
 * dragging its edge, or a place set by its move handle; one kind of pin either
 * way, holding the width AND the spot (`cabinetRunGeometry.withPinsPlaced`,
 * user ruling 2026-10-26) — and the click on it that lets the cabinet go.
 *
 * A fixed cabinet is a stop: the run packs around it, and a neighbour dragged
 * into it halts there ("…the next one, which holds its place"). With nothing
 * on the drawing to say which cabinets those are, a drag that stopped read as
 * mysterious (user report 2026-10-26). The engine already stamps the fact on
 * every such box (`category.fixedWidth`, `fillRun.emitBay` and the upper
 * tier), so this only shows it. Clicking the pin RELEASES the cabinet — the
 * same `{ width: null }` the panel's Width → Auto writes — so the run sizes
 * and places it again (user request, same day).
 *
 * Drawn on the editing views only — never in a PDF or a capture, where it
 * would read as part of the design. Sizes are SCREEN pixels, divided by the
 * view's scale, so the pin is the same size at every zoom.
 */

/** The pin's box, in screen px. */
export const FIXED_MARKER_PX = 18;
const INK = '#0047b3';

/** Is this a cabinet the user fixed? */
export const isFixedCabinet = (o) => o?.category?.fixedWidth === true;

/**
 * A push pin, drawn upright in a 24×24 box: the cap, the body narrowing to a
 * flared collar, and the needle. Tilted (`FIXED_MARKER.tiltDeg`) so it reads
 * as stuck in, not as a handle — the plan's and the elevation's handles are
 * all round.
 */
export const FIXED_MARKER = Object.freeze({
  // Cap, body and collar, outlined in white so the pin reads on any finish.
  body: 'M8 2H16V4H14.5V9L17 12V14H7V12L9.5 9V4H8Z',
  // The needle is a line in the pin's own ink: drawn as part of the outlined
  // shape, its point was all outline and vanished at drawing size.
  needle: Object.freeze({ x1: 12, y1: 13.5, x2: 12, y2: 22 }),
  box: 24,
  tiltDeg: 30,
  ink: INK,
});

/** The click target around a pin's anchor, in screen px. */
const HIT_PX = FIXED_MARKER_PX * 0.7;

/**
 * Where a cabinet's pin sits on the PLAN, world metres: inside its front
 * corner rather than its centre, so a click in the middle of the cabinet
 * still selects it. Local +X runs along the run, local +Z out of its front
 * (`objectFootprint`'s convention).
 */
export function fixedMarkerAnchor(obj, scale) {
  if (!obj?.center || !obj?.dimensions || !(scale > 0)) return null;
  const inset = HIT_PX / scale;
  const along = Math.max(0, (obj.dimensions.x || 0) / 2 - inset);
  const across = Math.max(0, (obj.dimensions.z || 0) / 2 - inset);
  const ry = obj.rotation?.y || 0;
  const dir = { x: Math.cos(ry), z: -Math.sin(ry) };
  const normal = { x: Math.sin(ry), z: Math.cos(ry) };
  return {
    x: obj.center.x + dir.x * along + normal.x * across,
    z: obj.center.z + dir.z * along + normal.z * across,
  };
}

function drawPin(ctx, at, scale) {
  const size = FIXED_MARKER_PX / scale; // world metres
  const k = size / FIXED_MARKER.box;
  ctx.save();
  ctx.translate(at.x, at.z);
  ctx.rotate((FIXED_MARKER.tiltDeg * Math.PI) / 180);
  ctx.scale(k, k);
  ctx.translate(-FIXED_MARKER.box / 2, -FIXED_MARKER.box / 2);
  const n = FIXED_MARKER.needle;
  ctx.strokeStyle = FIXED_MARKER.ink;
  ctx.lineWidth = 1.8;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(n.x1, n.y1);
  ctx.lineTo(n.x2, n.y2);
  ctx.stroke();
  ctx.fillStyle = FIXED_MARKER.ink;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1;
  ctx.lineJoin = 'round';
  if (typeof Path2D === 'function') {
    const p = new Path2D(FIXED_MARKER.body);
    ctx.stroke(p);
    ctx.fill(p);
  } else {
    // No Path2D (a test canvas): the head alone.
    ctx.beginPath();
    ctx.arc(12, 6, 5, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Draw pins on the plan and say where they were drawn — the click handler
 * tests against exactly this, so a pin that is not on screen (another floor,
 * the electrical drawing) cannot be clicked.
 *
 * @param {CanvasRenderingContext2D} ctx — world space (metres)
 * @param {Object[]} objects
 * @param {number} scale — canvas px per metre
 * @returns {{ object: Object, x: number, z: number, r: number }[]}
 */
export function drawFixedMarkers(ctx, objects, scale) {
  const hits = [];
  if (!(scale > 0)) return hits;
  for (const o of objects || []) {
    if (!isFixedCabinet(o)) continue;
    const at = fixedMarkerAnchor(o, scale);
    if (!at) continue;
    drawPin(ctx, at, scale);
    hits.push({ object: o, x: at.x, z: at.z, r: HIT_PX / scale });
  }
  return hits;
}

/** The cabinet whose pin is under `point` (world metres), or null. */
export function fixedPinAt(hits, point) {
  if (!hits?.length || !point) return null;
  for (const h of hits) {
    const dx = point.x - h.x;
    const dz = point.z - h.z;
    if (dx * dx + dz * dz <= h.r * h.r) return h.object;
  }
  return null;
}
